import {
  Input,
  inject,
  Output,
  Component,
  DestroyRef,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@services/logger.service';
import { readableTextColor } from 'src/app/shared/utils/color-utils';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { WidgetPersistenceService } from 'src/app/core/services/widget-persistence.service';

import { ObjectDefinition, Widget } from 'src/app/core/models/core.models';
import {
  WidgetType,
  NomSupportedLanguages,
  PopupType,
  NomObjectTypes,
  NomObjectClass,
} from 'src/app/core/constants';

import {
  selectCurrentObject,
  selectSelectedObjects,
  selectWidgetsLanguageId,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';

import {
  tap,
  map,
  filter,
  Observable,
  distinctUntilChanged,
  take,
  switchMap,
} from 'rxjs';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import { WidgetOperation } from 'src/app/core/models/widget-operations';
import { MoveCopyWidgetDialogComponent } from 'src/app/shared/components/move-copy-widget-dialog/move-copy-widget-dialog.component';
import { PickerDataobjectsDialogComponent } from '../../popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

@Component({
  selector: 'app-widget-card-list',
  templateUrl: './widget-card-list.component.html',
  styleUrls: ['./widget-card-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetCardListComponent {
  private readonly widgetPersistence = inject(WidgetPersistenceService);

  @Input() widget: Widget;
  @Input() objectActionService: ObjectActionsService;
  @Input() searchString: string;
  @Output() widgetSelected = new EventEmitter<boolean>();
  @Output() selectSectionBreak = new EventEmitter<Widget>();

  isSelected$: Observable<boolean>;
  currentLanguageId: number;
  currentLanguage: string;
  widgetTitle: string;
  exportWidgetTitle: string = '';

  protected destroyRef = inject(DestroyRef);
  protected translateService = inject(TranslateService);
  protected logger = inject(LoggerService);
  protected dialogService = inject(DialogService);
  widgetTypes = WidgetType;

  faGear = faGear;

  get headerColor(): string | null {
    return this.widget?.settings?.widget_header_color || null;
  }

  // * readable text color for the row when a custom background color is set
  get headerTextColor(): string | null {
    return this.headerColor ? readableTextColor(this.headerColor) : null;
  }

  constructor(
    private store: Store<AppState>,
    private editService: ObjectEditorService,
  ) {}

  ngOnInit() {
    if (this.widget) {
      this.widgetTitle = this.widget?.title;

      this.exportWidgetTitle =
        this.widget?.settings?.export_title?.[
          'language_id_' + this.currentLanguageId
        ] ?? '';
    }

    this.store
      .select(selectWidgetsLanguageId)
      .pipe(
        filter((id): id is number => id != null),
        distinctUntilChanged(),
        tap((id) => {
          const lang = NomSupportedLanguages.find((l) => l.id === id);

          this.currentLanguageId = id;
          this.currentLanguage = lang ? lang.name : '';

          this.exportWidgetTitle =
            this.widget?.settings?.export_title?.['language_id_' + id] ?? '';
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.isSelected$ = this.store.select(selectSelectedObjects).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((selectedObjects) => selectedObjects.has(this.widget.id)),
      distinctUntilChanged(),
    );
  }

  private saveWidget(widget: Widget): void {
    this.widgetPersistence.updateWidget(this.widget.id, widget);
  }

  onTitleBlur() {
    const updated = { ...this.widget, title: this.widgetTitle };
    console.log(updated.title);

    this.saveWidget(updated);
  }

  onExportTitleBlur() {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        export_title: {
          ...this.widget.settings.export_title,
          ['language_id_' + this.currentLanguageId]: this.exportWidgetTitle,
        },
      },
    };

    this.saveWidget(widgetCopy);
  }

  editWidget(widget, tab?: string) {
    this.editService.editWidget(widget, tab);
  }

  selectSectionHeading(widget) {
    this.objectActionService.toggleObjectSelection(widget);
    this.selectSectionBreak.emit(widget);
  }

  updatePageBreak(currentPageBreak: boolean) {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        section_break_page_break_before: !currentPageBreak,
      },
    };

    this.saveWidget(widgetCopy);
  }
  private buildObjectMap(objects?: any[]): Map<string, ObjectDefinition> {
    return new Map(
      objects?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]) || [],
    );
  }

  configurePopup(
    objectTypeIds: NomObjectTypes[],
    selectedObjectIds: Map<string, ObjectDefinition>,
    // * a `settings` patch for the picked ids, persisted by WidgetPersistenceService
    buildSettingsPatch: (objectIds: string[]) => Record<string, unknown>,
    popupType: PopupType,
  ) {
    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: { objectTypeIds, selectedObjectIds, popupType },
        height: '70vh',
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.pipe(take(1)).subscribe((event: any) => {
      if (
        event?.reason === DialogCloseReason.Save ||
        event?.reason === DialogCloseReason.SaveAndClose
      ) {
        this.widgetPersistence.applySettings(
          [this.widget.id],
          buildSettingsPatch(event?.data),
        );
      }

      console.log(
        '%c Dialog closed with event:',
        'color: green; font-weight: bold;',
        event,
      );
    });
  }

  configureDatasource() {
    this.configurePopup(
      [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.COMBINED_FEED,
      ],
      this.buildObjectMap(this.widget.datasources),
      (objectIds) => ({
        datasource_object_id: objectIds,
        datasource_object_class_id: Array(objectIds?.length).fill(
          NomObjectClass.FEED,
        ),
      }),
      PopupType.SELECT_DATASOURCES,
    );
  }

  configureFilters() {
    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      this.buildObjectMap(this.widget.dimensions_filter),
      (objectIds) => ({ dimension_id_filter: objectIds }),
      PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
    );
  }

  configurePivots() {
    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      this.buildObjectMap(this.widget.dimensions_pivot),
      (objectIds) => ({ dimension_id_pivot: objectIds }),
      PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
    );
  }

  duplicateWidget() {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        filter(Boolean),
        switchMap((currentObject) => {
          const dialogRef = this.dialogService.openSimpleDialog(
            MoveCopyWidgetDialogComponent,
            {
              data: {
                widgets: [this.widget],
                currentObject,
              },
              closeOnClick: false,
            },
          );

          return dialogRef.closed.pipe(take(1));
        }),
        filter(
          (result: DialogCloseOptions<{ data: WidgetOperation[] }>) =>
            result?.reason === DialogCloseReason.Save && !!result?.data?.data,
        ),
        tap((result) => {
          this.runWidgetMassOperation(result.data.data);
        }),
      )
      .subscribe();
  }

  private runWidgetMassOperation(operations: WidgetOperation[]) {
    this.widgetPersistence.massOperation(operations);
  }

  deleteWidget() {
    const message = this.translateService.instant(
      'widget-card.delete-widget.conformation-are-you-sure-you-want-to-delete-this-widget',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.deleteWidget(this.widget.id);
        } else if (event.reason === DialogCloseReason.Cancel) {
          this.logger.debug(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }
}
